import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { PageHeader, Loading, StatusBadge, Modal, Field, ErrBox, Empty } from "../../components/common";

const CATEGORIES = ["Account", "Tax Filing", "Tax Payment", "Merchant Connection", "Documents", "Billing", "Tax Rescue", "Other"];

export default function Support() {
  const [tickets, setTickets] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ category: "Account", subject: "", body: "" });
  const [reply, setReply] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/support/tickets").then((r) => setTickets(r.data.tickets));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await API.post("/support/tickets", form);
      toast.success("Support ticket created");
      setOpen(false); setForm({ category: "Account", subject: "", body: "" });
      load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await API.post(`/support/tickets/${active.id}/reply`, { body: reply });
      setReply("");
      const { data } = await API.get("/support/tickets");
      setTickets(data.tickets);
      setActive(data.tickets.find((t) => t.id === active.id));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!tickets) return <Loading label="Loading support tickets" />;

  return (
    <div data-testid="support-page">
      <PageHeader title="Customer Support" sub="Tickets are routed to the AZ Tax Accounts team."
        right={<button className="btn-navy" onClick={() => { setOpen(true); setErr(""); }} data-testid="new-ticket-btn"><Plus className="h-4 w-4" /> New Ticket</button>} />

      {tickets.length === 0 ? <Empty title="No tickets" msg="Need help? Open a ticket and we'll respond." /> : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => setActive(t)} className="card-gov flex w-full items-center justify-between px-4 py-3 text-left" data-testid={`ticket-${t.id}`}>
              <div>
                <div className="text-sm font-semibold text-slate-800">{t.subject}</div>
                <div className="text-xs text-slate-400">{t.category} · {fmtDate(t.created_at)} · {t.replies?.length || 0} replies</div>
              </div>
              <StatusBadge status={t.status} />
            </button>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Support Ticket">
        <form onSubmit={submit} className="space-y-4" data-testid="ticket-form">
          <Field label="Category">
            <select className="input-gov" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ticket-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Subject *"><input className="input-gov" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="ticket-subject" /></Field>
          <Field label="Details"><textarea className="input-gov" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ticket-body" /></Field>
          <ErrBox msg={err} />
          <button className="btn-navy w-full" disabled={busy} data-testid="ticket-submit">{busy ? "Creating…" : "Create Ticket"}</button>
        </form>
      </Modal>

      <Modal open={!!active} onClose={() => setActive(null)} title={active?.subject || ""}>
        {active && (
          <div className="space-y-4" data-testid="ticket-detail">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <StatusBadge status={active.status} /> {active.category} · opened {fmtDate(active.created_at)}
            </div>
            <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-700">{active.body || "(no details)"}</div>
            {(active.replies || []).map((r, i) => (
              <div key={i} className={`rounded-md p-3 text-sm ${STAFF(r.role) ? "ml-6 border border-sky-200 bg-sky-50 text-sky-900" : "mr-6 bg-slate-50 text-slate-700"}`}>
                <div className="mb-1 text-xs font-semibold">{r.by} · {fmtDate(r.at)}</div>{r.body}
              </div>
            ))}
            <div className="flex gap-2">
              <input className="input-gov" placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="ticket-reply-input" />
              <button className="btn-navy" onClick={sendReply} disabled={busy} data-testid="ticket-reply-send">Send</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function STAFF(role) {
  return ["SUPER_ADMIN", "ADMIN", "TAX_PROFESSIONAL", "ACCOUNT_MANAGER", "BOOKKEEPER", "SUPPORT"].includes(role);
}
